// src/pages/Warehouses/Reports/InventoryReport.jsx
// تقرير جرد المخزون — الرصيد الحالي لكل صنف مع التكلفة وقيمة البيع

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Autocomplete, TextField, Typography, Box } from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GridOnIcon from "@mui/icons-material/GridOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

import axios from "../../../axios";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useAllowedBranches, reportGridSx, fieldSx, fmtMoney, fmtNumber,
} from "../../../components/Reports/ReportKit";

// قراءة القيمة سواء جاءت camelCase أو PascalCase
const pick = (r, key) => {
  const v = r[key] ?? r[key.charAt(0).toUpperCase() + key.slice(1)];
  return v ?? 0;
};

const moneyCell = (color, bold = 700) => (p) => (
  <Typography sx={{
    direction: "ltr", fontWeight: bold, fontSize: 13.5,
    color: Number(p.value) < 0 ? "#be123c" : Number(p.value) === 0 ? "#cbd5e1" : color,
  }}>
    {fmtMoney(p.value)}
  </Typography>
);

const qtyCell = (color) => (p) => (
  <Typography sx={{ direction: "ltr", fontWeight: 700, fontSize: 13.5, color: Number(p.value) === 0 ? "#cbd5e1" : color }}>
    {fmtNumber(p.value)}
  </Typography>
);

export default function InventoryReport() {
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const { branchOptions, defaultBranch, ready, branchName } = useAllowedBranches(companyId);

  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const [product, setProduct] = useState(null);
  const [branchId, setBranchId] = useState("");
  const [stockId, setStockId] = useState("");
  const [balance, setBalance] = useState("");
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    setProductsLoading(true);
    Promise.all([
      axios.get(`/Product/by-company/${companyId}`),
      axios.get(`/Stock/by-company/${companyId}`),
    ])
      .then(([p, s]) => { setProducts(p.data || []); setStocks(s.data || []); })
      .catch(() => {})
      .finally(() => setProductsLoading(false));
  }, [companyId]);

  useEffect(() => { if (ready) setBranchId(defaultBranch); }, [ready, defaultBranch]);

  const productIdOf = (p) => p?.id ?? p?.productId;

  const fetchReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const pid = productIdOf(product);
      const params = {
        ...(pid && { productId: pid }),
        ...(branchId && { branchId }),
        ...(stockId && { stockId }),
      };
      const res = await axios.get(`/StockMovements/inventory-report/${companyId}`, { params });
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data.map((r, i) => ({
        id: i + 1,
        productName: r.productName ?? r.ProductName,
        stockName: r.stockName ?? r.StockName,
        branchName: r.branchName ?? r.BranchName,
        totalIn: Number(pick(r, "totalIn")) || 0,
        totalOut: Number(pick(r, "totalOut")) || 0,
        closingStock: Number(pick(r, "closingStock")) || 0,
        purchasePrice: Number(pick(r, "purchasePrice")) || 0,
        totalPurchaseCost: Number(pick(r, "totalPurchaseCost")) || 0,
        salePrice: Number(pick(r, "salePrice")) || 0,
        totalSaleValue: Number(pick(r, "totalSaleValue")) || 0,
      })));
    } catch {
      toast.error("فشل تحميل التقرير");
    } finally {
      setLoading(false);
    }
  }, [companyId, product, branchId, stockId]);

  useEffect(() => { if (ready) fetchReport(); }, [ready, fetchReport]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (q && !(r.productName ?? "").toLowerCase().includes(q)) return false;
      if (balance === "positive" && r.closingStock <= 0) return false;
      if (balance === "zero" && r.closingStock !== 0) return false;
      if (balance === "negative" && r.closingStock >= 0) return false;
      return true;
    });
  }, [rows, search, balance]);

  const stats = useMemo(() => {
    const t = visibleRows.reduce((a, r) => ({
      stock: a.stock + r.closingStock,
      cost: a.cost + r.totalPurchaseCost,
      value: a.value + r.totalSaleValue,
      negative: a.negative + (r.closingStock < 0 ? 1 : 0),
    }), { stock: 0, cost: 0, value: 0, negative: 0 });
    return { ...t, profit: t.value - t.cost };
  }, [visibleRows]);

  const columns = useMemo(() => [
    {
      field: "productName", headerName: "الصنف", flex: 1.6, minWidth: 190, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{p.value || "—"}</Typography>,
    },
    { field: "stockName", headerName: "المستودع", flex: 1, minWidth: 120, headerAlign: "center", align: "center" },
    { field: "branchName", headerName: "الفرع", flex: 1, minWidth: 110, headerAlign: "center", align: "center" },
    { field: "totalIn", headerName: "الوارد", type: "number", width: 90, headerAlign: "center", align: "center", renderCell: qtyCell("#047857") },
    { field: "totalOut", headerName: "الصادر", type: "number", width: 90, headerAlign: "center", align: "center", renderCell: qtyCell("#be123c") },
    {
      field: "closingStock", headerName: "الرصيد", type: "number", width: 105, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const v = Number(p.value) || 0;
        return (
          <Box sx={{
            px: 1.25, py: .3, borderRadius: "8px", fontWeight: 800, fontSize: 13.5, direction: "ltr", minWidth: 48, textAlign: "center",
            bgcolor: v > 0 ? "#d1fae5" : v < 0 ? "#ffe4e6" : "#f1f5f9",
            color: v > 0 ? "#047857" : v < 0 ? "#be123c" : "#94a3b8",
          }}>
            {fmtNumber(v)}
          </Box>
        );
      },
    },
    { field: "purchasePrice", headerName: "سعر الشراء", type: "number", width: 110, headerAlign: "center", align: "center", renderCell: moneyCell("#475569", 600) },
    { field: "totalPurchaseCost", headerName: "إجمالي التكلفة", type: "number", width: 130, headerAlign: "center", align: "center", renderCell: moneyCell("#0f172a") },
    { field: "salePrice", headerName: "سعر البيع", type: "number", width: 110, headerAlign: "center", align: "center", renderCell: moneyCell("#4338ca", 600) },
    { field: "totalSaleValue", headerName: "قيمة البيع", type: "number", width: 130, headerAlign: "center", align: "center", renderCell: moneyCell("#059669", 800) },
  ], []);

  const exportExcel = () => {
    if (!visibleRows.length) return toast.info("لا توجد بيانات للتصدير");
    const data = visibleRows.map(r => ({
      "الصنف": r.productName,
      "المستودع": r.stockName,
      "الفرع": r.branchName,
      "الوارد": r.totalIn,
      "الصادر": r.totalOut,
      "الرصيد": r.closingStock,
      "سعر الشراء": r.purchasePrice,
      "إجمالي التكلفة": r.totalPurchaseCost,
      "سعر البيع": r.salePrice,
      "قيمة البيع": r.totalSaleValue,
    }));
    data.push({
      "الصنف": "الإجمالي", "الرصيد": stats.stock,
      "إجمالي التكلفة": stats.cost, "قيمة البيع": stats.value,
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!views"] = [{ RTL: true }];
    ws["!cols"] = Object.keys(data[0]).map(k => ({ wch: k === "الصنف" ? 32 : 14 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "جرد المخزون");
    XLSX.writeFile(wb, `Inventory_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const resetFilters = () => {
    setBranchId(defaultBranch);
    setStockId("");
    setProduct(null);
    setBalance("");
    setSearch("");
  };

  const bName = branchName(branchId);
  const activeCount = (branchId ? 1 : 0) + (stockId ? 1 : 0) + (product ? 1 : 0) + (balance ? 1 : 0) + (search ? 1 : 0);

  return (
    <ReportPage
      title="جرد المخزون"
      subtitle="الرصيد الحالي لكل صنف مع تكلفته وقيمة بيعه"
      icon={<InventoryIcon />}
      badges={[
        { label: `حتى ${dayjs().format("DD/MM/YYYY HH:mm")}`, icon: <EventOutlinedIcon /> },
        ...(bName ? [{ label: bName, icon: <StorefrontOutlinedIcon /> }] : []),
      ]}
      actions={[
        { label: "تحديث", icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: "Excel", icon: <GridOnIcon />, onClick: exportExcel, primary: true },
      ]}
      stats={[
        { label: "عدد الأصناف", value: fmtNumber(visibleRows.length), icon: <CategoryOutlinedIcon />, color: "primary" },
        { label: "إجمالي الرصيد", value: fmtNumber(stats.stock), icon: <Inventory2OutlinedIcon />, color: "slate" },
        { label: "إجمالي التكلفة", value: fmtMoney(stats.cost), icon: <PaymentsOutlinedIcon />, color: "warning" },
        { label: "قيمة البيع", value: fmtMoney(stats.value), icon: <SellOutlinedIcon />, color: "info" },
        { label: "أرصدة سالبة", value: fmtNumber(stats.negative), icon: <WarningAmberIcon />, color: "error" },
        { label: "الربح المتوقع", value: fmtMoney(stats.profit), icon: <TrendingUpIcon />, color: "success", highlight: true },
      ]}
      filters={
        <ReportFilters
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="اسم الصنف..."
          onApply={fetchReport}
          onReset={resetFilters}
          loading={loading}
          activeCount={activeCount}
        >
          <ReportSelect label="الفرع" value={branchId} onChange={setBranchId} options={branchOptions} />
          <ReportSelect
            label="المستودع" value={stockId} onChange={setStockId}
            options={stocks.map(s => ({ value: String(s.id), label: s.nameAr || s.name }))}
          />
          <ReportSelect
            label="حالة الرصيد" value={balance} onChange={setBalance}
            options={[
              { value: "positive", label: "متوفر (أكبر من صفر)" },
              { value: "zero", label: "نفد (صفر)" },
              { value: "negative", label: "سالب" },
            ]}
          />
          <Autocomplete
            size="small"
            options={products}
            value={product}
            loading={productsLoading}
            loadingText="جاري التحميل..."
            onChange={(_, v) => setProduct(v)}
            getOptionLabel={(p) => p?.arabicName || p?.nameAr || ""}
            isOptionEqualToValue={(a, b) => productIdOf(a) === productIdOf(b)}
            noOptionsText="لا توجد نتائج"
            renderOption={(props, option) => (
              <li {...props} key={productIdOf(option)}>
                <Box>
                  <Typography sx={{ fontSize: 14 }}>{option.arabicName}</Typography>
                  {option.code && <Typography sx={{ fontSize: 12, color: "#64748b" }}>كود: {option.code}</Typography>}
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="الصنف" placeholder="الكل" sx={fieldSx} />}
          />
        </ReportFilters>
      }
    >
      <ReportTableCard title="أرصدة الأصناف" count={fmtNumber(visibleRows.length)}>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          getRowClassName={(p) => (p.row.closingStock < 0 ? "row-negative" : "")}
          localeText={{
            noRowsLabel: "لا توجد أصناف",
            toolbarColumns: "الأعمدة",
            toolbarFilters: "الفلاتر",
            toolbarDensity: "الكثافة",
            toolbarExport: "تصدير",
          }}
          sx={{ ...reportGridSx, "& .row-negative": { bgcolor: "#fff7f8 !important" } }}
        />
      </ReportTableCard>
    </ReportPage>
  );
}